import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { clearRecordRefs, loadRecordRefs } from '../utils/recordRefs';
import { getActiveProjectId } from '../utils/session';

/**
 * Keeps the record numbers (PO-130 rather than PO-90F0D6B4) loaded for the
 * active project. Renders nothing. Loads once sign-in and a project are known,
 * again when the project changes, and when the tab regains focus (the store
 * itself limits refreshes to one a minute). Forgets the numbers on sign-out.
 */
export default function RecordRefsLoader(): null {
  const { isAuthenticated } = useAuth();
  const { activeProjectId } = useProject();

  useEffect(() => {
    if (!isAuthenticated) {
      clearRecordRefs();
      return undefined;
    }
    // The stored project is read directly: AuthContext writes it before the
    // project context has caught up.
    const load = () => { void loadRecordRefs(getActiveProjectId() ?? activeProjectId); };
    load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, activeProjectId]);

  return null;
}
